import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useTheme } from 'styled-components';
import { RightSideCards } from '../LeadDetails/LeadDetailsEdit/leadDetailsEdit.styles';
import usePromotions from '../../hooks/usePromotions';

const formatDate = dateStr => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const Promotions = props => {
  const { tenantId, apartmentId } = props;
  const theme = useTheme();

  const {
    data: promotions,
    isLoading,
    isError
  } = usePromotions({
    clientId: tenantId
  });

  const promotion = useMemo(() => {
    if (!promotions) return null;
    const communityPromotions = promotions.filter(
      p => p.isActive && p.communityId === apartmentId
    );
    if (communityPromotions.length === 0) return null;
    return communityPromotions.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];
  }, [promotions, apartmentId]);

  return (
    <RightSideCards className="right-side-cards">
      <Box sx={{ mb: 2 }}>
        <Typography className="card-title" sx={{ color: theme?.text?.heading }}>
          Promotions
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.primary
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Loading promotions
          </Typography>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.error || '#ff4d4f',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Failed to load promotions
          </Typography>
          <Typography sx={{ fontSize: '12px', mt: 0.625, opacity: 0.8 }}>
            Please try refreshing the page or contact support if the issue
            persists.
          </Typography>
        </Box>
      ) : promotion ? (
        <Box
          sx={{
            p: 2,
            borderRadius: '8px',
            border: `1px solid ${theme?.border?.primary || '#eee'}`,
            background: theme?.background?.tertiary
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5
            }}
          >
            <LocalOfferIcon
              sx={{ fontSize: '18px', color: theme?.text?.link || '#5267FF' }}
            />
            <Chip
              label="Active"
              size="small"
              color="success"
              sx={{ fontSize: '10px', height: '20px' }}
            />
            {promotion.source && (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: theme?.text?.secondary || '#888',
                  textTransform: 'capitalize',
                  ml: 'auto'
                }}
              >
                {promotion.source}
              </Typography>
            )}
          </Box>

          <Typography
            sx={{
              fontSize: '13px',
              color: theme?.text?.primary,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap'
            }}
          >
            {promotion.description}
          </Typography>

          {promotion.createdAt && (
            <Typography
              sx={{
                fontSize: '11px',
                color: '#888',
                mt: 1.5,
                pt: 1,
                borderTop: `1px solid ${theme?.border?.primary || '#eee'}`
              }}
            >
              Since {formatDate(promotion.createdAt)}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            color: theme?.text?.secondary || '#888'
          }}
        >
          <LocalOfferIcon sx={{ fontSize: '32px', opacity: 0.3, mb: 1 }} />
          <Typography sx={{ fontSize: '14px' }}>
            No active promotion for this community.
          </Typography>
        </Box>
      )}
    </RightSideCards>
  );
};

export default Promotions;
